import styled, { keyframes } from "styled-components";

// ============ Animations ============

export const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

export const gridPulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.5;
  }
`;

export const particleFloat = keyframes`
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) translateX(50px);
    opacity: 0;
  }
`;

// ============ Layout Components ============

export const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 20px 40px; /* Added top padding for fixed nav */
  background: ${props => props.$isDark 
    ? 'radial-gradient(ellipse at top left, #1a0a3e 0%, #0a0a0f 35%, #0a0a0f 65%, #0f1a2e 100%)'
    : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%, #f9fafb 100%)'
  };
  text-align: center;
  position: relative;
  overflow: hidden;

  /* Cosmic gradient overlays */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$isDark ? `
      radial-gradient(ellipse at 20% 30%, rgba(74, 158, 255, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 70%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 80%, rgba(16, 217, 127, 0.1) 0%, transparent 50%),
      radial-gradient(ellipse at 60% 20%, rgba(253, 176, 34, 0.08) 0%, transparent 50%)
    ` : 'transparent'};
    pointer-events: none;
    z-index: 0;
  }

  /* Animated grid pattern */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(${props => props.$isDark ? 'rgba(74, 158, 255, 0.15)' : props.$colors?.border || '#e5e7eb'} 1px, transparent 1px),
      linear-gradient(90deg, ${props => props.$isDark ? 'rgba(74, 158, 255, 0.15)' : props.$colors?.border || '#e5e7eb'} 1px, transparent 1px);
    background-size: 25px 25px;
    animation: ${gridPulse} 4s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }
`;

export const ParticlesLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
  
  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle, ${props => props.$isDark ? 'rgba(74, 158, 255, 0.4)' : 'rgba(59, 130, 246, 0.3)'} 2px, transparent 2px),
      radial-gradient(circle, ${props => props.$isDark ? 'rgba(139, 92, 246, 0.4)' : 'rgba(139, 92, 246, 0.3)'} 1px, transparent 1px),
      radial-gradient(circle, ${props => props.$isDark ? 'rgba(16, 217, 127, 0.4)' : 'rgba(16, 185, 129, 0.3)'} 1.5px, transparent 1.5px);
    background-size: 400px 400px, 300px 300px, 350px 350px;
    background-position: 0 0, 100px 100px, 200px 50px;
    animation: ${particleFloat} 20s linear infinite;
    opacity: ${props => props.$isDark ? '0.6' : '0.3'};
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  padding-top: 40px;
`;

// ============ Typography ============

export const Title = styled.h1`
  font-family: inherit;
  font-size: 3.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px;
  text-align: center;
  letter-spacing: -0.5px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

export const Description = styled.p`
  font-family: inherit;
  color: #94a3b8;
  font-size: 1.25rem;
  font-weight: 300;
  max-width: 600px;
  margin: 0 auto 64px;
  line-height: 1.6;
  text-align: center;
`;

// ============ Feature Cards ============

export const FeatureCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled.div`
  background: rgba(17, 24, 39, 0.4); /* Transparent dark background */
  backdrop-filter: blur(12px); /* Glass effect */
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  /* Subtle gradient overlay */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%);
    pointer-events: none;
  }

  /* Top highlight line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${props => props.$accentColor}, transparent);
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    background: rgba(17, 24, 39, 0.6);
    border-color: ${props => props.$accentColor};
    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.2), 0 0 15px ${props => props.$accentColor}20;

    &::before {
      opacity: 1;
      height: 2px;
    }
  }
`;

export const FeatureIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: ${props => props.$bgDark}; /* Keep the colored background */
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
  transition: transform 0.3s ease;

  ${FeatureCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }

  svg {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
  }
`;

export const FeatureTitle = styled.h3`
  font-family: inherit;
  font-size: 1.25rem;
  font-weight: 500;
  color: ${props => props.$isDark ? '#f3f4f6' : '#111827'};
  margin: 0 0 8px;
`;

export const FeatureDesc = styled.p`
  font-family: inherit;
  font-size: 0.875rem;
  color: ${props => props.$isDark ? '#9ca3af' : '#6b7280'};
  margin: 0;
  line-height: 1.5;
`;

// ============ CTA Section ============

export const CTASection = styled.div`
  margin-top: 64px;
  width: 100%;
  background: rgba(17, 24, 39, 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 40px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.3);

  /* Glowing orb effect behind */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -20%;
    width: 60%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
    transform: rotate(30deg);
    pointer-events: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
    padding: 24px;
    gap: 24px;
  }
`;

export const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CTATextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CTATitle = styled.h4`
  font-family: inherit;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
`;

export const CTASubtitle = styled.p`
  font-family: inherit;
  font-size: 1.125rem;
  color: #94a3b8;
  margin: 0;
`;

export const CTAButton = styled.button`
  position: relative;
  z-index: 1;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 14px 32px;
  font-family: inherit;
  font-weight: 600;
  border-radius: 12px;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3), inset 0 1px 0 rgba(255,255,255,0.2);

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255,255,255,0.2);
  }
`;

// ============ Info Section ============

export const InfoSection = styled.div`
  margin-top: 64px;
  width: 100%;
  border-top: 1px solid ${props => props.$isDark ? '#1f2937' : '#e5e7eb'};
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const InfoTitle = styled.h3`
  font-family: inherit;
  font-size: 1.125rem;
  font-weight: 500;
  color: ${props => props.$isDark ? '#e5e7eb' : '#374151'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  width: 100%;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const StepNumber = styled.span`
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => props.$isDark ? '#64748b' : '#94a3b8'};
  border: 1px solid ${props => props.$isDark ? '#334155' : '#e2e8f0'};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StepText = styled.p`
  font-family: inherit;
  font-size: 1rem;
  color: ${props => props.$isDark ? '#cbd5e1' : '#475569'};
  margin: 0;
`;

export const Footer = styled.p`
  margin-top: 80px;
  color: ${props => props.$isDark ? '#4b5563' : '#9ca3af'};
  font-size: 0.875rem;
  font-family: inherit;
`;
